
import {
  ellipsisString,
  formatDateNormal,
  formatInputByCountry,
  formatPriceRP,
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { SuccessBooking } from "@/domain/entities/CreateBookingV3";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { Vue, Options, prop } from "vue-class-component";
import { createFocusTrap } from "focus-trap";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { PaymentController } from "@/app/ui/controllers/PaymentController";
import formatInputMoneyWithDecimal from "@/app/infrastructures/misc/common-library/FormatInputMoneyWithDecimal";

class Props {
  data = prop<SuccessBooking>({
    required: true
  });
  formType = prop<string>({
    default: "manual",
    required: true
  });
  isButtonCreateInvoice = prop<boolean>({
    default: true,
    type: Boolean
  });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  prefixCurrency = prop<string>({ required: true, type: String });
  clientCode = prop<string>({ default: "", type: String });
}

@Options({
  emits: ["print", "close", "previewInvoice"]
})
class ModalSuccess extends Vue.with(Props) {
  focusTrap: any = null;
  messageEntities = new ModalMessageEntities();

  async mounted() {
    this.setBookingSuccessModal();
    await this.$nextTick();
    this.focusTrap = createFocusTrap((this.$refs["modal-ref"] as any).$el, {
      escapeDeactivates: true,
      allowOutsideClick: true,
      returnFocusOnDeactivate: true,
      clickOutsideDeactivates: false,
      onDeactivate: () => {
        this.$emit("close");
      }
    });
    this.focusTrap.activate();
  }

  unmounted() {
    this.deactivateFocusTrap();
  }

  get detailData() {
    return this.data;
  }

  get detailStt() {
    return this.detailData.stt[0];
  }

  get isClientID() {
    const clientCountry = this.clientCode;
    if (!clientCountry) return true;
    return clientCountry.toLowerCase() === "id";
  }
  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get isPos() {
    return AccountController.accountData.isAllPartner;
  }

  get isInternal() {
    return AccountController.accountData.isInternalAccount;
  }

  get estimationDate() {
    const isMonthYearIsSame =
      formatDateNormal(
        this.detailData.estimationDate.split(" - ")[0],
        "MMMM YYYY"
      ) ===
      formatDateNormal(
        this.detailData.estimationDate.split(" - ")[1],
        "MMMM YYYY"
      );
    const estimationDateStart = formatDateNormal(
      this.detailData.estimationDate.split(" - ")[0],
      `${isMonthYearIsSame ? "D" : "D MMMM YYYY"}`
    );
    const estimationDateEnd = formatDateNormal(
      this.detailData.estimationDate.split(" - ")[1],
      "D MMMM YYYY"
    );

    return `${estimationDateStart} - ${estimationDateEnd}`;
  }

  setBookingSuccessModal() {
    this.messageEntities = new ModalMessageEntities({
      customWidth: "640px",
      image: "badge-success",
      imageWidth: "24",
      imageheight: "24",
      title: this.$t("bookingV3.modalSuccess.title")
      // textSuccess: this.$t('bookingV3.modalSuccess.print'),
      // textCancel: this.$t('bookingV3.modalSuccess.close'),
      // onSubmit: () => {
      //   this.deactivateFocusTrap()
      //   this.$emit("print", this.detailStt.sttId, this.detailStt.sttNo);
      //   this.$emit("close");
      // },
    });
  }
  onClose() {
    this.deactivateFocusTrap();
    this.$emit("close");

    if (this.showCreateInvoice) {
      PrintReceiptController.setVisibleToastHistorySuccessBooking(true);
    }
  }

  onSubmit() {
    this.deactivateFocusTrap();
    this.$emit("print", this.detailStt.sttId, this.detailStt.sttNo);
    this.$emit("close");
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (AccountController.accountData?.accountCurrency ?? "IDR") as
      | "IDR"
      | "MYR";
  }

  formatPriceRP(price: any) {
    return formatPriceRP(price, this.accountCurrency);
  }

  get formatHidePrice() {
    if (this.isCrossCountryCod) return `${this.prefixCurrency}••••••`;

    return !this.isAccountForeign ? "Rp••••••" : "RM••••••";
  }

  formatPriceCurrency(value: number): string {
    if (this.isCrossCountryCod) {
      return `${this.prefixCurrency}${formatInputByCountry(value, {
        type: "money",
        country: this.prefixCurrency === "Rp" ? "ID" : "MY"
      })}`;
    }

    return this.formatPriceRP(value);
  }

  get isBookingClient() {
    return (
      this.formType === "client" || AccountController.accountData.isAllClient
    );
  }

  get recipientName() {
    return ellipsisString(this.detailData.recipientName, 10);
  }

  generateDiscountElement() {
    if (
      !this.detailData.listDiscount.length ||
      this.detailStt.totalBeforeDiscount === this.detailStt.totalAfterDiscount
    )
      return "";

    return (
      this.detailData.listDiscount
        .map((discount: any, index: number) => {
          return `
        <div class="flex flex-row items-center gap-1 px-1 rounded bg-green-lp-700 text-white text-10px">
          <div>${discount.config.pdc_promo_discount}% Off</div>
          <img class="w-4 h-4" src="${require("@/app/ui/assets/svg/information-circle-green-1.svg")}" />
        </div>
        ${
          index !== this.detailData.listDiscount.length - 1
            ? '<div class="text-base font-semibold text-gray-lp-600">+</div>'
            : ""
        }
        `;
        })
        .join("") +
      `
        <div class="text-sm text-gray-lp-600">
          <s>${formatPriceRP(
            this.detailStt.totalBeforeDiscount,
            this.accountCurrency
          )}</s>
        </div>
      `
    );
  }

  deactivateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap = null;
    }
  }

  get showCreateInvoice() {
    const flag = FlagsPermissionBooking.flag_booking_print_invoice_receipt_enabled.isEnabled();
    const { isPosAccount } = AccountController.accountData;
    return flag && isPosAccount && this.isButtonCreateInvoice;
  }
  onPreviewInvoice() {
    this.onClose();
    this.$emit("previewInvoice", this.detailStt.sttId, this.detailStt.sttNo);
    PrintReceiptController.setVisiblePreviewInvoice(true);
    PrintReceiptController.setProcessInvoiceFromSuccessBooking(true);
  }
}

export default ModalSuccess;
